import { useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SpeedIcon from '@mui/icons-material/Speed';
import type { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { graphql } from '../../../gql';
import { DataGrid } from '../../../components/data-grid';
import {
  DateInput,
  LoadingOverlay,
  displayCoordinate,
  displayDate,
  displayDecimal,
  displayInteger,
  displayPercent,
  displayText,
} from '../../../components/ui';

const LITRES_PER_US_GALLON = 3.785411784;

const defaultTelematicsColumns = [
  'totalFuelLitres',
  'totalIdleHours',
  'dailyIdleHours',
  'idlePercent',
  'totalPayloadTonnes',
  'dailyPayloadTonnes',
  'payloadPerCycleTonnes',
  'totalDistanceKm',
  'dailyDistanceKm',
];

const ASSET_CLOCK_READING_HISTORY_QUERY = graphql(`
  query AssetClockReadingHistory($assetId: Int!, $fromDate: LocalDate, $toDate: LocalDate) {
    assetClockReadingHistory(assetId: $assetId, fromDate: $fromDate, toDate: $toDate) {
      fromDate
      toDate
      isTelematicsAsset
      rows {
        id readingDate smr runtime fuelLitres fuelGallons consumptionLitresPerHour consumptionGallonsPerHour
        replacedClock replacedClockLatestSmr startingSmr totalFuelLitres totalIdleHours dailyIdleHours idlePercent
        totalPayloadTonnes dailyPayloadTonnes payloadPerCycleTonnes totalDistanceKm dailyDistanceKm latitude longitude
        altitudeMetres defRemainingTankCapacityLitres defRemainingPercent fuelRemainingTankCapacityLitres
        fuelRemainingPercent fuelUsedLast24HoursLitres maximumSpeedLast24 comment
      }
    }
  }
`);

function displayConvertedFuel(value: unknown, showGallons: boolean): string {
  const litres = typeof value === 'number' ? value : null;
  return displayDecimal(litres === null || !showGallons ? litres : litres / LITRES_PER_US_GALLON);
}

function createColumns(showGallons: boolean, selectedTelematicsColumns: string[]): ColDef[] {
  const fuelUnit = showGallons ? 'US gal' : 'L';
  const fuelValue = (litresField: string, gallonsField?: string) => (params: { data?: Record<string, unknown> }) =>
    gallonsField && showGallons
      ? displayDecimal(params.data?.[gallonsField] as number | null | undefined)
      : displayConvertedFuel(params.data?.[litresField], showGallons);
  const optional = (field: string, headerName: string, valueFormatter: ColDef['valueFormatter']): ColDef => ({
    field,
    headerName,
    hide: !selectedTelematicsColumns.includes(field),
    sortable: false,
    valueFormatter,
  });

  return [
    { field: 'readingDate', headerName: 'Reading Date', flex: 1, valueFormatter: (params) => displayDate(params.value as string | null | undefined) },
    {
      field: 'smr',
      headerName: 'SMR',
      flex: 1,
      cellRenderer: (params: { value: unknown; data?: Record<string, unknown> }) => {
        const replacementDetails = params.data?.replacedClock
          ? `Replaced Clock: SMR Replaced - ${params.data.replacedClockLatestSmr ?? '-'}; SMR Installed - ${params.data.startingSmr ?? '-'}`
          : null;
        return replacementDetails ? (
          <span className="inline-flex items-center gap-1">
            {displayInteger(params.value as number | null | undefined)}
            <Tooltip title={replacementDetails}>
              <IconButton aria-label={replacementDetails} size="small"><SpeedIcon fontSize="small" /></IconButton>
            </Tooltip>
          </span>
        ) : displayInteger(params.value as number | null | undefined);
      },
    },
    { field: 'runtime', headerName: 'Runtime [h]', flex: 1, valueFormatter: (params) => displayInteger(params.value as number | null | undefined) },
    { field: 'fuelLitres', headerName: `Fuel [${fuelUnit}]`, flex: 1, valueFormatter: fuelValue('fuelLitres', 'fuelGallons') },
    { field: 'consumptionLitresPerHour', headerName: `Fuel Burn [${fuelUnit}/h]`, flex: 1, valueFormatter: fuelValue('consumptionLitresPerHour', 'consumptionGallonsPerHour') },
    optional('totalFuelLitres', `Total Fuel [${fuelUnit}]`, (params) => displayConvertedFuel(params.value, showGallons)),
    optional('totalIdleHours', 'Total Idle [h]', (params) => displayDecimal(params.value as number | null | undefined)),
    optional('dailyIdleHours', 'Daily Idle [h]', (params) => displayDecimal(params.value as number | null | undefined)),
    optional('idlePercent', 'Idle [%]', (params) => displayPercent(params.value as number | null | undefined)),
    optional('totalPayloadTonnes', 'Total Payload [t]', (params) => displayDecimal(params.value as number | null | undefined)),
    optional('dailyPayloadTonnes', 'Daily Payload [t]', (params) => displayDecimal(params.value as number | null | undefined)),
    optional('payloadPerCycleTonnes', 'Payload per Cycle [t]', (params) => displayDecimal(params.value as number | null | undefined)),
    optional('totalDistanceKm', 'Total Distance [km]', (params) => displayDecimal(params.value as number | null | undefined)),
    optional('dailyDistanceKm', 'Daily Distance [km]', (params) => displayDecimal(params.value as number | null | undefined)),
    optional('latitude', 'Latitude', (params) => displayCoordinate(params.value as number | null | undefined)),
    optional('longitude', 'Longitude', (params) => displayCoordinate(params.value as number | null | undefined)),
    optional('altitudeMetres', 'Altitude [m]', (params) => displayDecimal(params.value as number | null | undefined)),
    optional('defRemainingTankCapacityLitres', `DEF Remaining Tank [${fuelUnit}]`, (params) => displayConvertedFuel(params.value, showGallons)),
    optional('defRemainingPercent', 'DEF Remaining [%]', (params) => displayPercent(params.value as number | null | undefined)),
    optional('fuelRemainingTankCapacityLitres', `Fuel Remaining Tank [${fuelUnit}]`, (params) => displayConvertedFuel(params.value, showGallons)),
    optional('fuelRemainingPercent', 'Fuel Remaining [%]', (params) => displayPercent(params.value as number | null | undefined)),
    optional('fuelUsedLast24HoursLitres', `Fuel Used Last 24h [${fuelUnit}]`, (params) => displayConvertedFuel(params.value, showGallons)),
    optional('maximumSpeedLast24', 'MAX Speed Last 24h', (params) => displayDecimal(params.value as number | null | undefined)),
    optional('comment', 'Comment', (params) => displayText(params.value as string | null | undefined)),
  ];
}

/** Read-only Clock Reading history for the current Asset. */
export function AssetClockReadingsTab() {
  const { assetId } = useParams();
  const id = Number(assetId);
  const [draftFrom, setDraftFrom] = useState<string | null>(null);
  const [draftTo, setDraftTo] = useState<string | null>(null);
  const [appliedRange, setAppliedRange] = useState<{ fromDate: string | null; toDate: string | null }>({ fromDate: null, toDate: null });
  const [showGallons, setShowGallons] = useState(false);
  const [selectedTelematicsColumns, setSelectedTelematicsColumns] = useState(defaultTelematicsColumns);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<HTMLElement | null>(null);
  const gridApi = useRef<GridApi | null>(null);
  const invalidRange = draftFrom !== null && draftTo !== null && draftFrom > draftTo;

  const { data, loading, error } = useQuery(ASSET_CLOCK_READING_HISTORY_QUERY, {
    variables: { assetId: id, fromDate: appliedRange.fromDate, toDate: appliedRange.toDate },
    skip: !Number.isInteger(id) || id <= 0,
  });

  const history = data?.assetClockReadingHistory;
  const rows = history?.rows ?? [];
  const columns = createColumns(showGallons, selectedTelematicsColumns);
  const optionalColumnFields = columns.slice(5).map((column) => column.field as string);
  const allTelematicsColumnsSelected = optionalColumnFields.every((field) => selectedTelematicsColumns.includes(field));

  const apply = () => {
    if (!invalidRange) setAppliedRange({ fromDate: draftFrom, toDate: draftTo });
  };
  const clear = () => {
    setDraftFrom(null);
    setDraftTo(null);
    setAppliedRange({ fromDate: null, toDate: null });
    gridApi.current?.setFilterModel(null);
  };
  const toggleTelematicsColumn = (field: string) => {
    setSelectedTelematicsColumns((selected) => selected.includes(field)
      ? selected.filter((selectedField) => selectedField !== field)
      : [...selected, field]);
  };
  const toggleAllTelematicsColumns = () => setSelectedTelematicsColumns(
    allTelematicsColumnsSelected ? [] : optionalColumnFields,
  );
  const onGridReady = (event: GridReadyEvent) => {
    gridApi.current = event.api;
  };

  return (
    <section className="py-6" aria-label="Clock Readings history">
      <LoadingOverlay visible={loading} />
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <DateInput label="From" value={draftFrom} onChange={setDraftFrom} />
        <DateInput label="To" value={draftTo} onChange={setDraftTo} />
        <Button variant="outlined" onClick={clear}>Clear</Button>
        <Button variant="contained" onClick={apply} disabled={invalidRange}>Filter</Button>
        {history && <>
          <FormControlLabel
            control={<Switch checked={showGallons} onChange={(_, checked) => setShowGallons(checked)} />}
            label="Show values in US gallons"
          />
          <Button aria-haspopup="menu" aria-label="Select telematics columns" onClick={(event) => setColumnMenuAnchor(event.currentTarget)}>
            Columns
          </Button>
          <Menu
            anchorEl={columnMenuAnchor}
            open={columnMenuAnchor !== null}
            onClose={() => setColumnMenuAnchor(null)}
            slotProps={{
              paper: {
                sx: {
                  height: 360,
                  maxHeight: 360,
                  overflowY: 'auto',
                  backgroundColor: 'var(--container_background_elevated)',
                  border: '1px solid var(--border)',
                  color: 'var(--foreground)',
                  '& .MuiMenuItem-root': {
                    color: 'var(--foreground)',
                    '&:hover': { backgroundColor: 'var(--container_background_hover)' },
                    '&.Mui-selected': { backgroundColor: 'var(--container_background_selected)' },
                  },
                  '& .MuiCheckbox-root': { color: 'var(--foreground_faded)' },
                  '& .MuiCheckbox-root.Mui-checked': { color: 'var(--primary)' },
                },
              },
            }}
          >
            <MenuItem role="menuitemcheckbox" aria-checked={allTelematicsColumnsSelected} onClick={toggleAllTelematicsColumns}>
              <Checkbox checked={allTelematicsColumnsSelected} />Select all
            </MenuItem>
            {columns.slice(5).map((column) => (
              <MenuItem
                key={column.field}
                role="menuitemcheckbox"
                aria-checked={selectedTelematicsColumns.includes(column.field as string)}
                onClick={() => toggleTelematicsColumn(column.field as string)}
              >
                <Checkbox checked={selectedTelematicsColumns.includes(column.field as string)} />
                {column.headerName}
              </MenuItem>
            ))}
          </Menu>
        </>}
      </div>
      {invalidRange && <Typography color="error" role="alert">End date must be on or after start date.</Typography>}
      {error && <Typography color="error" role="alert">{error.message}</Typography>}
      {history && rows.length === 0 && <Typography color="text.secondary">No clock readings match the current period.</Typography>}
      {rows.length > 0 && <DataGrid rowData={rows} columnDefs={columns} onGridReady={onGridReady} height="65vh" />}
    </section>
  );
}